import { useMemo, useState } from 'react';
import type { QrDoProduto } from '../../api/produtos.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { CampoAreaTexto, CampoSelecao } from '../../componentes/Campo.js';
import { Cartao } from '../../componentes/Cartao.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeEtiqueta } from '../../componentes/Icones.js';
import { BuscaNoCadastro } from './BuscaNoCadastro.js';
import type { Recusa } from './etiqueta.js';
import {
  acrescentarCodigos,
  COPIAS_MAXIMAS,
  etiquetasParaImprimir,
  LIMITE_DE_PRODUTOS,
  TAMANHO_PADRAO,
  TAMANHOS_DE_ETIQUETA,
  tamanhoDeEtiqueta,
} from './etiqueta.js';
import { FolhaDeImpressao } from './FolhaDeImpressao.js';
import { OrientacaoDeImpressao } from './OrientacaoDeImpressao.js';
import { PreviaDasEtiquetas } from './PreviaDasEtiquetas.js';
import { useQrDosProdutos } from './useQrDosProdutos.js';
import './GeradorDeEtiquetas.css';

const OPCOES_DE_TAMANHO = TAMANHOS_DE_ETIQUETA.map((tamanho) => ({
  valor: tamanho.id,
  rotulo: tamanho.rotulo,
}));

const OPCOES_DE_COPIAS = Array.from({ length: COPIAS_MAXIMAS }, (_, indice) => {
  const copias = indice + 1;
  return {
    valor: String(copias),
    rotulo: copias === 2 ? '2 — frente e lateral' : String(copias),
  };
});

function plural(quantidade: number, singular: string, varias: string): string {
  return `${String(quantidade)} ${quantidade === 1 ? singular : varias}`;
}

/**
 * Montar a lista de produtos, conferir as etiquetas e mandar para a
 * impressão. Só existe para quem pode (`etiquetas.imprimir`).
 */
export function GeradorDeEtiquetas() {
  const [codigos, definirCodigos] = useState<readonly string[]>([]);
  const [texto, definirTexto] = useState('');
  const [recusados, definirRecusados] = useState<readonly Recusa[]>([]);
  const [idDoTamanho, definirIdDoTamanho] = useState<string>(TAMANHO_PADRAO);
  const [copias, definirCopias] = useState(1);

  const { estados, tentarDeNovo } = useQrDosProdutos(codigos);
  const tamanho = tamanhoDeEtiqueta(idDoTamanho);

  const qrsProntos = useMemo(
    () =>
      new Map(
        codigos.flatMap((codPro): [string, QrDoProduto][] => {
          const estado = estados.get(codPro);
          return estado?.situacao === 'pronto' ? [[codPro, estado.qr]] : [];
        }),
      ),
    [codigos, estados],
  );

  const prontos = codigos.filter((codPro) => qrsProntos.has(codPro));
  const semQr = codigos.filter((codPro) => estados.get(codPro)?.situacao === 'falhou').length;
  const gerando = codigos.length - prontos.length - semQr;
  const etiquetas = etiquetasParaImprimir(prontos, copias);

  function acrescentar(entrada: string) {
    const resultado = acrescentarCodigos(codigos, entrada);
    definirCodigos(resultado.codigos);
    definirRecusados(resultado.recusados);
  }

  return (
    <div className="gerador-etiquetas">
      <div className="gerador-etiquetas__colunas">
        <Cartao titulo="Produtos">
          <div className="gerador-etiquetas__entrada">
            <BuscaNoCadastro naLista={codigos} aoEscolher={acrescentar} />

            <CampoAreaTexto
              rotulo="Códigos dos produtos"
              valor={texto}
              aoMudar={definirTexto}
              linhas={4}
              ajuda={`Um codPro por linha, como está no ERP — até ${String(LIMITE_DE_PRODUTOS)} por impressão`}
            />
            <div>
              <Botao
                onClick={() => {
                  acrescentar(texto);
                  definirTexto('');
                }}
              >
                Adicionar à lista
              </Botao>
            </div>

            {recusados.length > 0 && (
              <AvisoDaTela tom="atencao">
                Não entraram na lista:
                {recusados.map((recusa) => (
                  <span key={recusa.codigo} className="gerador-etiquetas__recusa">
                    {`${recusa.codigo} — ${recusa.motivo}`}
                  </span>
                ))}
              </AvisoDaTela>
            )}
          </div>
        </Cartao>

        <Cartao titulo="Impressão">
          <div className="gerador-etiquetas__impressao">
            <CampoSelecao
              rotulo="Tamanho da etiqueta"
              valor={tamanho.id}
              opcoes={OPCOES_DE_TAMANHO}
              aoMudar={definirIdDoTamanho}
              ajuda="Uma etiqueta por página, no tamanho exato do rolo"
            />
            <CampoSelecao
              rotulo="Cópias por produto"
              valor={String(copias)}
              opcoes={OPCOES_DE_COPIAS}
              aoMudar={(valor) => {
                definirCopias(Number(valor));
              }}
            />

            {semQr > 0 && (
              <AvisoDaTela tom="atencao">
                {semQr === 1
                  ? '1 produto sem QR fica fora da impressão.'
                  : `${String(semQr)} produtos sem QR ficam fora da impressão.`}
              </AvisoDaTela>
            )}

            {etiquetas.length > 0 && gerando === 0 && (
              <Botao
                variante="primario"
                onClick={() => {
                  window.print();
                }}
              >
                {`Imprimir ${plural(etiquetas.length, 'etiqueta', 'etiquetas')}`}
              </Botao>
            )}
          </div>
        </Cartao>
      </div>

      {codigos.length === 0 ? (
        <Cartao semRespiro>
          <EstadoVazio
            tom="neutro"
            icone={<IconeEtiqueta tamanho={22} />}
            titulo="Nenhum produto na lista"
            detalhe="Busque no cadastro ou digite os códigos. Cada produto ganha uma etiqueta com o QR no padrão da plataforma e o código legível abaixo."
          />
        </Cartao>
      ) : (
        <PreviaDasEtiquetas
          codigos={codigos}
          estados={estados}
          tamanho={tamanho}
          aoRemover={(codPro) => {
            definirCodigos((atuais) => atuais.filter((codigo) => codigo !== codPro));
          }}
          aoTentarDeNovo={tentarDeNovo}
        />
      )}

      <OrientacaoDeImpressao />

      <FolhaDeImpressao tamanho={tamanho} etiquetas={etiquetas} qrs={qrsProntos} />
    </div>
  );
}
